import React, { useCallback, useEffect, useRef, useState } from "react";
import { UploadCloud, Search, Trash2, Copy, Check, Loader2, X, Image as ImgIcon, Tags, Folder, FolderOpen, Filter } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { API_BASE } from "../DataContext";

const fmtBytes = (n) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};
const fmtDate = (iso) => new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

export function AdminImages({ token, onUnauthorized }) {
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(48);
  const [q, setQ] = useState("");
  const [folder, setFolder] = useState("");
  const [tag, setTag] = useState("");
  const [facets, setFacets] = useState({ folders: [], tags: [] });
  const [selected, setSelected] = useState(new Set());
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // { total, done, errors:[] }
  const [uploadFolder, setUploadFolder] = useState("");
  const [uploadTags, setUploadTags] = useState("");
  const [copied, setCopied] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [detail, setDetail] = useState(null);
  const fileInputRef = useRef(null);

  const auth = { Authorization: `Bearer ${token}` };
  const publicOrigin = typeof window !== "undefined" ? window.location.origin : "";

  const fetchList = useCallback(async () => {
    const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
    if (q.trim()) params.set("q", q.trim());
    if (folder) params.set("folder", folder);
    if (tag) params.set("tag", tag);
    const r = await fetch(`${API_BASE}/api/admin/images?${params}`, { headers: auth });
    if (r.status === 401) return onUnauthorized?.();
    const data = await r.json();
    setItems(data.items || []);
    setTotal(data.total || 0);
  }, [page, pageSize, q, folder, tag, token]); // eslint-disable-line

  const fetchFacets = useCallback(async () => {
    const r = await fetch(`${API_BASE}/api/admin/images/facets`, { headers: auth });
    if (r.ok) setFacets(await r.json());
  }, [token]); // eslint-disable-line

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { fetchFacets(); }, [fetchFacets, items]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const doUpload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    setUploadProgress({ total: files.length, done: 0, errors: [] });
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      if (uploadFolder.trim()) fd.append("folder", uploadFolder.trim());
      if (uploadTags.trim()) fd.append("tags", uploadTags.trim());
      const r = await fetch(`${API_BASE}/api/admin/images/bulk`, {
        method: "POST", headers: auth, body: fd,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Upload failed");
      setUploadProgress({ total: data.summary.total, done: data.summary.ok, errors: data.errors });
      await fetchList();
      // Auto-clear after 3.5s if no errors
      if (!data.errors?.length) setTimeout(() => setUploadProgress(null), 3500);
    } catch (err) {
      alert(err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const copyUrl = async (path) => {
    const full = `${publicOrigin}${path}`;
    try {
      await navigator.clipboard.writeText(full);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = full; document.body.appendChild(ta); ta.select();
      document.execCommand("copy"); document.body.removeChild(ta);
    }
    setCopied(path);
    setTimeout(() => setCopied((c) => (c === path ? "" : c)), 1400);
  };

  const remove = async (img) => {
    if (!window.confirm(`Delete "${img.original_name || img.filename}"? This cannot be undone.`)) return;
    const r = await fetch(`${API_BASE}/api/admin/images/${img.id}`, { method: "DELETE", headers: auth });
    if (!r.ok) return alert("Delete failed");
    setDetail(null); fetchList();
  };

  const bulkDelete = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Delete ${selected.size} image(s)? This cannot be undone.`)) return;
    const r = await fetch(`${API_BASE}/api/admin/images/bulk-delete`, {
      method: "POST", headers: { ...auth, "Content-Type": "application/json" },
      body: JSON.stringify({ ids: [...selected] }),
    });
    if (!r.ok) return alert("Bulk delete failed");
    setSelected(new Set()); fetchList();
  };

  const toggleSelect = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    if (e.dataTransfer?.files?.length) doUpload(e.dataTransfer.files);
  };

  const clearFilters = () => { setQ(""); setFolder(""); setTag(""); setPage(1); };

  return (
    <div className="space-y-4" data-testid="admin-images">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-bold text-[#0a2a5e] flex items-center gap-2">
            <ImgIcon className="h-5 w-5" /> Image Manager
          </h2>
          <p className="text-xs text-slate-500">Upload JPG / PNG / WebP — auto-converts to WebP @ 85% quality. URLs are public.</p>
        </div>
        {selected.size > 0 && (
          <Button onClick={bulkDelete} data-testid="bulk-delete-btn" className="rounded-full bg-red-600 hover:bg-red-700 text-white">
            <Trash2 className="h-4 w-4 mr-1" /> Delete ({selected.size})
          </Button>
        )}
      </div>

      {/* Upload zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`rounded-2xl border-2 border-dashed p-6 transition-colors ${
          dragOver ? "border-sky-500 bg-sky-50" : "border-slate-200 bg-white"
        }`}
        data-testid="upload-zone"
      >
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[240px]">
            <div className="flex items-center gap-2 mb-3">
              <UploadCloud className="h-5 w-5 text-sky-600" />
              <p className="text-sm font-semibold text-slate-800">Drag & drop images here, or click Browse</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              <input
                type="text" value={uploadFolder} onChange={(e) => setUploadFolder(e.target.value)}
                placeholder='Folder (e.g. "blog", "products") — optional'
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
                data-testid="upload-folder-input"
              />
              <input
                type="text" value={uploadTags} onChange={(e) => setUploadTags(e.target.value)}
                placeholder="Tags, comma-separated (e.g. hero, cover, banner)"
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
                data-testid="upload-tags-input"
              />
            </div>
          </div>
          <div>
            <input
              ref={fileInputRef} type="file" multiple accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
              onChange={(e) => doUpload(e.target.files)} className="hidden" data-testid="upload-file-input"
            />
            <Button onClick={() => fileInputRef.current?.click()} disabled={uploading}
              className="rounded-full bg-[#0a2a5e] hover:bg-[#0a2a5e]/90 text-white" data-testid="browse-btn">
              {uploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <UploadCloud className="h-4 w-4 mr-1" />}
              {uploading ? "Uploading…" : "Browse Files"}
            </Button>
          </div>
        </div>
        {uploadProgress && (
          <div className="mt-4 text-xs">
            <div className="flex items-center gap-2 text-emerald-700">
              <Check className="h-4 w-4" /> Uploaded {uploadProgress.done}/{uploadProgress.total}
              {uploadProgress.errors?.length ? <span className="text-red-600 ml-2">· {uploadProgress.errors.length} failed</span> : null}
            </div>
            {uploadProgress.errors?.length > 0 && (
              <ul className="mt-2 space-y-0.5 text-red-600">
                {uploadProgress.errors.map((e, i) => <li key={i}>• {e.filename}: {e.error}</li>)}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-2xl p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder="Search filename or tag…" className="pl-9" data-testid="images-search" />
        </div>
        <select value={folder} onChange={(e) => { setFolder(e.target.value); setPage(1); }}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white" data-testid="images-folder-filter">
          <option value="">All folders</option>
          {facets.folders.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
        <select value={tag} onChange={(e) => { setTag(e.target.value); setPage(1); }}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white" data-testid="images-tag-filter">
          <option value="">All tags</option>
          {facets.tags.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        {(q || folder || tag) && (
          <Button variant="outline" onClick={clearFilters} className="rounded-full text-xs">
            <X className="h-3 w-3 mr-1" /> Clear
          </Button>
        )}
        <div className="ml-auto text-xs text-slate-500">{total} image{total === 1 ? "" : "s"}</div>
      </div>

      {/* Grid */}
      {items === null && <div className="text-center text-slate-400 py-16">Loading…</div>}
      {items && items.length === 0 && (
        <div className="text-center py-16 bg-white rounded-2xl border border-slate-100">
          <ImgIcon className="h-10 w-10 mx-auto text-slate-300 mb-2" />
          <p className="text-slate-500 text-sm">No images yet. Upload your first one above.</p>
        </div>
      )}
      {items && items.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
          {items.map((img) => {
            const isSel = selected.has(img.id);
            return (
              <div key={img.id}
                className={`group relative bg-white rounded-xl border overflow-hidden transition ${
                  isSel ? "border-sky-500 ring-2 ring-sky-200" : "border-slate-200 hover:border-sky-300"
                }`}
                data-testid={`image-card-${img.id}`}
              >
                <button onClick={() => toggleSelect(img.id)}
                  className={`absolute top-1.5 left-1.5 z-10 h-5 w-5 rounded border-2 flex items-center justify-center transition ${
                    isSel ? "bg-sky-500 border-sky-500 text-white" : "bg-white/90 border-slate-300 opacity-0 group-hover:opacity-100"
                  }`}
                  aria-label="Select">
                  {isSel && <Check className="h-3 w-3" />}
                </button>
                <div className="aspect-square bg-slate-50 flex items-center justify-center cursor-pointer" onClick={() => setDetail(img)}>
                  <img src={img.path} alt={img.original_name} loading="lazy" decoding="async" className="max-w-full max-h-full object-contain" />
                </div>
                <div className="px-2.5 py-2 border-t border-slate-100">
                  <div className="text-[11px] font-semibold text-slate-800 truncate" title={img.original_name}>{img.original_name || img.filename}</div>
                  <div className="text-[10px] text-slate-500 flex items-center justify-between mt-0.5">
                    <span>{img.width}×{img.height}</span>
                    <span>{fmtBytes(img.size_bytes)}</span>
                  </div>
                  <div className="flex gap-1 mt-1.5">
                    <button
                      onClick={(e) => { e.stopPropagation(); copyUrl(img.path); }}
                      className={`flex-1 flex items-center justify-center gap-1 rounded-md text-[10px] font-semibold py-1 transition ${
                        copied === img.path ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-700 hover:bg-sky-500 hover:text-white"
                      }`}
                      data-testid={`copy-url-${img.id}`}
                    >
                      {copied === img.path ? <><Check className="h-3 w-3" /> Copied</> : <><Copy className="h-3 w-3" /> Copy URL</>}
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); remove(img); }}
                      className="rounded-md bg-slate-100 text-red-600 hover:bg-red-500 hover:text-white px-1.5 transition"
                      data-testid={`delete-${img.id}`}
                      title="Delete"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {items && items.length > 0 && totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(1)}>« First</Button>
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>‹ Prev</Button>
          <span className="text-xs text-slate-500 px-2">Page {page} of {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next ›</Button>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(totalPages)}>Last »</Button>
        </div>
      )}

      {/* Detail modal */}
      {detail && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
              <h3 className="text-base font-bold text-[#0a2a5e] truncate">{detail.original_name || detail.filename}</h3>
              <button onClick={() => setDetail(null)} className="text-slate-400 hover:text-slate-700 p-1"><X className="h-5 w-5" /></button>
            </div>
            <div className="grid md:grid-cols-2 gap-4 p-5">
              <div className="bg-slate-50 rounded-lg flex items-center justify-center min-h-[240px]">
                <img src={detail.path} alt={detail.original_name} className="max-w-full max-h-[60vh] object-contain" />
              </div>
              <div className="space-y-3 text-sm">
                <MetaRow label="Dimensions" value={`${detail.width} × ${detail.height} px`} />
                <MetaRow label="File size" value={fmtBytes(detail.size_bytes)} />
                <MetaRow label="Format" value="WebP (85%)" />
                <MetaRow label="Uploaded" value={fmtDate(detail.uploaded_at)} />
                {detail.uploaded_by && <MetaRow label="By" value={detail.uploaded_by} />}
                <MetaRow label="Folder" value={detail.folder || <span className="text-slate-400">—</span>} icon={<Folder className="h-3.5 w-3.5" />} />
                <MetaRow label="Tags" value={
                  (detail.tags?.length ? detail.tags.join(", ") : <span className="text-slate-400">—</span>)
                } icon={<Tags className="h-3.5 w-3.5" />} />
                <div>
                  <div className="text-xs font-semibold text-slate-500 mb-1">Public URL</div>
                  <div className="flex gap-1">
                    <input
                      readOnly
                      value={`${publicOrigin}${detail.path}`}
                      className="flex-1 bg-slate-50 border border-slate-200 rounded px-2 py-1.5 text-xs font-mono"
                      onFocus={(e) => e.target.select()}
                      data-testid="detail-url-input"
                    />
                    <Button
                      onClick={() => copyUrl(detail.path)}
                      className={`rounded-md text-xs h-8 px-3 ${copied === detail.path ? "bg-emerald-500 hover:bg-emerald-600" : "bg-sky-500 hover:bg-sky-600"} text-white`}
                      data-testid="detail-copy-btn"
                    >
                      {copied === detail.path ? <><Check className="h-3 w-3 mr-1" /> Copied</> : <><Copy className="h-3 w-3 mr-1" /> Copy</>}
                    </Button>
                  </div>
                </div>
                <div className="pt-2 flex justify-between border-t border-slate-100">
                  <a href={detail.path} target="_blank" rel="noreferrer" className="text-xs text-sky-600 hover:underline">Open in new tab ↗</a>
                  <button onClick={() => remove(detail)} className="text-xs text-red-600 hover:underline flex items-center gap-1">
                    <Trash2 className="h-3 w-3" /> Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const MetaRow = ({ label, value, icon }) => (
  <div className="flex items-center justify-between border-b border-slate-100 pb-1.5">
    <span className="text-xs font-semibold text-slate-500 flex items-center gap-1">{icon}{label}</span>
    <span className="text-sm text-slate-800">{value}</span>
  </div>
);
